import { useState, useMemo } from "react";
import { Box, Text, VStack, HStack, Radio, RadioGroup } from "@chakra-ui/react";
import { CustomModal } from "components/custom-modal";
import { createModalTrigger, useModalControls } from "hooks/use-modal-controls";
import { apiPipeline } from "api/api-pipeline";
import { Toast } from "components/toast";
import { StatesMachine } from "../../classes/state-machine/state-machine";
import { getStateStats } from "./DiagnosticPanel";
import { ButtonWithLoading } from "components/mailing-components/mailing-table/icon-button-with-loading";
import { CheckIcon } from "@chakra-ui/icons";

export const CenarioValue = {
  PORTABILIDADE_PURA: "PORTABILIDADE_PURA",
  BASE_ESTAGNADA: "BASE_ESTAGNADA",
  REFINANCIAMENTO_COM_TROCO: "REFINANCIAMENTO_COM_TROCO",
  REFINANCIAMENTO_REDUCAO_COM_TROCO: "REFINANCIAMENTO_REDUCAO_COM_TROCO",
  CONTRATO_NOVO: "CONTRATO_NOVO",
  INCONCLUSIVO: "INCONCLUSIVO"
} as const;

export type CenarioValue = (typeof CenarioValue)[keyof typeof CenarioValue];

type SelectedOption =
  | "REFINANCIAMENTO"
  | "PORTABILIDADE_PURA"
  | "CONTRATO_NOVO";

const CENARIO_TO_CODE: Record<CenarioValue, number> = {
  [CenarioValue.PORTABILIDADE_PURA]: 601,
  [CenarioValue.BASE_ESTAGNADA]: 600,
  [CenarioValue.REFINANCIAMENTO_COM_TROCO]: 602,
  [CenarioValue.REFINANCIAMENTO_REDUCAO_COM_TROCO]: 605,
  [CenarioValue.CONTRATO_NOVO]: 603,
  [CenarioValue.INCONCLUSIVO]: 604
};

const OPTION_TO_PERFIS: Record<SelectedOption, CenarioValue[]> = {
  REFINANCIAMENTO: [
    CenarioValue.REFINANCIAMENTO_COM_TROCO,
    CenarioValue.REFINANCIAMENTO_REDUCAO_COM_TROCO
  ],
  PORTABILIDADE_PURA: [CenarioValue.PORTABILIDADE_PURA],
  CONTRATO_NOVO: [CenarioValue.CONTRATO_NOVO]
};

interface CenarioOption {
  id: SelectedOption;
  label: string;
  description: string;
  color: string;
}

const CENARIO_OPTIONS: CenarioOption[] = [
  {
    id: "REFINANCIAMENTO",
    label: "Refinanciamento",
    description: "Refinanciamento com Troco e Redução com Troco",
    color: "#38A169"
  },
  {
    id: "PORTABILIDADE_PURA",
    label: "Portabilidade",
    description: "Priorizar para redução de parcela",
    color: "#3182CE"
  },
  {
    id: "CONTRATO_NOVO",
    label: "Contrato Novo",
    description: "Separar para operação nova",
    color: "#00B5D8"
  }
];

interface EscolherCenariosParams {
  stateId: number;
  pipelineData: StatesMachine;
}

const eventName = "openEscolherCenariosModal";

export const openEscolherCenariosModal =
  createModalTrigger<EscolherCenariosParams>(eventName);

export function EscolherCenariosModal({
  diagnosticUpdatePipelineData
}: {
  diagnosticUpdatePipelineData:
    | ((updater: (draft: StatesMachine) => StatesMachine | void) => void)
    | undefined;
}) {
  const { isOpen, onClose, params } =
    useModalControls<EscolherCenariosParams>(eventName);
  const [selectedOption, setSelectedOption] = useState<SelectedOption | null>(
    null
  );
  const [loading, setLoading] = useState(false);

  const eventTotals = useMemo(() => {
    const map = new Map<number, number>();
    if (!params.pipelineData || !params.stateId) return map;
    const stats = getStateStats(
      params.stateId,
      "ANALISE_IA",
      params.pipelineData
    );
    stats?.events.forEach((e) => {
      if (e.code != null) map.set(e.code, e.total);
    });
    return map;
  }, [params.pipelineData, params.stateId]);

  function getCpfs(option: SelectedOption) {
    return OPTION_TO_PERFIS[option].reduce(
      (sum, cenario) => sum + (eventTotals.get(CENARIO_TO_CODE[cenario]) ?? 0),
      0
    );
  }

  async function handleStep2() {
    if (!params.stateId || !params.pipelineData || !selectedOption) return;
    setLoading(true);
    try {
      const perfis = OPTION_TO_PERFIS[selectedOption];
      const queryString = perfis.map((p) => `perfis=${p}`).join("&");
      await apiPipeline.put(
        `/pipeline-new/state-ia/${params.stateId}/continue?${queryString}`
      );

      diagnosticUpdatePipelineData?.((draft) => {
        const state = draft.getState(params.stateId!);
        if (state) state.paused = false;
      });
      onClose();
    } catch (err) {
      console.error("Erro ao processar cenários:", err);
      Toast({ title: "Erro ao confirmar", status: "error" });
    } finally {
      setLoading(false);
    }
  }

  const modalTitle = (
    <Box>
      <Text fontWeight="700" fontSize="md" color="gray.800">
        Escolher cenários para seguir
      </Text>
      <Text fontWeight="400" fontSize="xs" color="gray.500" mt="2px">
        A próxima etapa receberá somente os cenários selecionados.
      </Text>
    </Box>
  );

  return (
    <CustomModal
      isOpen={isOpen}
      onClose={onClose}
      size="3xl"
      modalTitle={modalTitle}
      modalFooter={
        <ButtonWithLoading
          size="sm"
          isLoading={loading}
          isDisabled={!selectedOption}
          onClick={handleStep2}
          rightIcon={<CheckIcon />}
        >
          Confirmar
        </ButtonWithLoading>
      }
    >
      <RadioGroup
        value={selectedOption ?? ""}
        onChange={(value) => setSelectedOption(value as SelectedOption)}
      >
        <VStack spacing={2} align="stretch">
          {CENARIO_OPTIONS.map((cenario) => {
            const isChecked = selectedOption === cenario.id;
            return (
              <Box
                key={cenario.id}
                border="1.5px solid"
                borderColor={isChecked ? cenario.color : "gray.200"}
                borderRadius="10px"
                px={3}
                py="10px"
                cursor="pointer"
                bg={isChecked ? `${cenario.color}08` : "white"}
                transition="border-color 0.15s, background 0.15s"
                onClick={() => setSelectedOption(cenario.id)}
                _hover={{
                  borderColor: isChecked ? cenario.color : "gray.300"
                }}
              >
                <HStack justify="space-between" align="center">
                  <HStack spacing={3}>
                    <Radio
                      value={cenario.id}
                      onClick={(e) => e.stopPropagation()}
                      colorScheme={
                        cenario.color === "#38A169"
                          ? "green"
                          : cenario.color === "#3182CE"
                            ? "blue"
                            : cenario.color === "#00B5D8"
                              ? "cyan"
                              : "orange"
                      }
                    />
                    <Box>
                      <Text fontWeight="600" fontSize="sm" color="gray.800">
                        {cenario.label}
                      </Text>
                      <Text fontSize="xs" color="gray.400">
                        {cenario.description}
                      </Text>
                    </Box>
                  </HStack>
                  <Text
                    fontWeight="700"
                    fontSize="sm"
                    color={cenario.color}
                    whiteSpace="nowrap"
                  >
                    {getCpfs(cenario.id).toLocaleString("pt-BR")} CPFs
                  </Text>
                </HStack>
              </Box>
            );
          })}
        </VStack>
      </RadioGroup>
    </CustomModal>
  );
}
